import { useState } from 'react'
import { Building2, CalendarDays, ClipboardCheck, Pencil, Quote } from 'lucide-react'
import type {
  Attendance,
  AttendanceBreak,
  AttendanceUserSummary,
  PopulatedShiftAssignmentRef,
} from '@/features/attendance/types/attendance.types'
import { Badge, Button, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { Avatar } from '@/components/common/Avatar'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { TimeCorrectionRequestModal } from './TimeCorrectionRequestModal'
import { EditAttendanceTimesModal } from './EditAttendanceTimesModal'
import { attendanceStatusBadgeVariant } from '@/features/attendance/utils/attendanceErrors'
import {
  ATTENDANCE_STATUS_LABEL,
  attendanceUserName,
  dayOffset,
  formatWorkedMinutes,
  getScheduledWindow,
  isCurrentlyClockedIn,
} from '@/features/attendance/utils/attendanceDisplay'
import { formatDate, formatDateTime, userSubline } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import { useAttendanceById } from '@/features/attendance/hooks/useAttendance'
import { usePermissions } from '@/hooks/usePermissions'
import { useMe } from '@/features/settings/hooks/useMe'

function isPopulatedShiftAssignment(
  val: string | PopulatedShiftAssignmentRef | undefined
): val is PopulatedShiftAssignmentRef {
  return val != null && typeof val === 'object' && '_id' in val
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/[_-]/g, ' ')
}

function breakMinutes(breaks: AttendanceBreak[]): number {
  return breaks.reduce((sum, b) => sum + (b.duration ?? 0), 0)
}

export interface AttendanceDetailModalProps {
  companyId: string
  attendanceId: string | null
  /** Shown in the employee header; the modal has no company lookup of its own. */
  companyName?: string
  /** The list row's user summary — the detail endpoint doesn't always populate it. */
  user?: AttendanceUserSummary | null
  onClose: () => void
}

export function AttendanceDetailModal({
  companyId,
  attendanceId,
  companyName,
  user,
  onClose,
}: AttendanceDetailModalProps): React.ReactElement | null {
  const { can } = usePermissions()
  const { data: me } = useMe()
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const { data, isLoading, error } = useAttendanceById(companyId, attendanceId)
  const attendance = data?.data
  // Employees request corrections on their own record; managers/HR/admins with
  // attendance:update edit other people's records directly (server refuses self-edits).
  const isOwnRecord = !!me && !!attendance && attendance.userId === me._id
  const canRequestCorrection = isOwnRecord && can('time_corrections', 'create_self')
  const canEditAttendance = !isOwnRecord && can('attendance', 'update')

  if (!attendanceId) return null

  const footer = attendance ? (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      {canRequestCorrection ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Times look wrong? A correction goes to a manager for review.
        </p>
      ) : canEditAttendance ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Edits recalculate worked time and are audited.
        </p>
      ) : (
        <span />
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
        {canRequestCorrection && (
          <Button type="button" variant="primary" onClick={() => setCorrectionModalOpen(true)}>
            <Pencil className="h-4 w-4" aria-hidden />
            <span className="ml-1.5">Request correction</span>
          </Button>
        )}
        {canEditAttendance && (
          <Button type="button" variant="primary" onClick={() => setEditModalOpen(true)}>
            <Pencil className="h-4 w-4" aria-hidden />
            <span className="ml-1.5">Edit times</span>
          </Button>
        )}
      </div>
    </div>
  ) : null

  return (
    <>
      <Modal
        open={!!attendanceId}
        onClose={onClose}
        title="Attendance record"
        className="max-w-2xl"
        footer={footer}
      >
        {isLoading ? (
          <SectionLoader variant="detail" label="Loading attendance record" />
        ) : error ? (
          <QueryErrorState error={error} size="inline" title="Couldn't load this attendance record" />
        ) : attendance ? (
          <div className="space-y-5">
            <EmployeeHeader
              attendance={attendance}
              user={attendance.user ?? user ?? null}
              companyName={companyName}
            />
            <ScheduleVsActual attendance={attendance} />
            <SessionsTable attendance={attendance} />
            <TimeSummary attendance={attendance} />
            {attendance.notes && <NotesBlock notes={attendance.notes} />}
          </div>
        ) : null}
      </Modal>
      {companyId && attendanceId && (
        <TimeCorrectionRequestModal
          companyId={companyId}
          attendanceId={attendanceId}
          open={correctionModalOpen}
          onClose={() => setCorrectionModalOpen(false)}
          onSuccess={() => setCorrectionModalOpen(false)}
        />
      )}
      {companyId && attendance && editModalOpen && (
        <EditAttendanceTimesModal
          companyId={companyId}
          attendance={attendance}
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
        />
      )}
    </>
  )
}

function EmployeeHeader({
  attendance,
  user,
  companyName,
}: {
  attendance: Attendance
  user: AttendanceUserSummary | null
  companyName?: string
}): React.ReactElement {
  const name = attendanceUserName({ ...attendance, user })
  const subline = userSubline(user?.employeeId, user?.email)
  const live = isCurrentlyClockedIn(attendance)
  const assignment = isPopulatedShiftAssignment(attendance.shiftAssignmentId)
    ? attendance.shiftAssignmentId
    : null

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/40">
      <div className="flex items-start gap-3">
        <Avatar name={name} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
            <Badge variant={attendanceStatusBadgeVariant(attendance.status)} dot>
              {ATTENDANCE_STATUS_LABEL[attendance.status] ?? attendance.status}
            </Badge>
            {live && (
              <Badge variant="success">
                <span className="relative flex h-1.5 w-1.5" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60 motion-reduce:animate-none" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                </span>
                On the clock
              </Badge>
            )}
          </div>
          {subline && (
            <p className="truncate text-sm text-gray-600 dark:text-gray-400">{subline}</p>
          )}
          {attendance.isEdited && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Pencil className="h-3 w-3" aria-hidden />
              Edited
              {attendance.editedAt && <> {formatDateTime(attendance.editedAt)}</>}
            </p>
          )}
        </div>
      </div>
      <dl className="mt-3 grid gap-2 border-t border-gray-200 pt-3 text-sm sm:grid-cols-3 dark:border-gray-700">
        <MetaItem icon={Building2} label="Company" value={companyName ?? '—'} />
        <MetaItem
          icon={CalendarDays}
          label="Shift date"
          value={attendance.date ? formatDate(attendance.date.slice(0, 10), 'EEE, d MMM yyyy') : '—'}
        />
        <MetaItem
          icon={ClipboardCheck}
          label="Assignment"
          value={assignment?.status ? capitalize(assignment.status) : '—'}
        />
      </dl>
    </div>
  )
}

function MetaItem({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ComponentType<{ className?: string }>
  label: string
  value: string
}): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {label}
      </dt>
      <dd className="truncate font-medium text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  )
}

type DiffTone = 'ok' | 'warn' | 'muted'

/**
 * "6h 03m late", "12m early", "On time" (within a minute either way).
 * `concern` is the direction that matters: arriving late, or leaving early.
 */
function describeDiff(
  actualMs: number,
  scheduledMs: number,
  concern: 'late' | 'early'
): { label: string; tone: DiffTone } {
  const minutes = Math.round((actualMs - scheduledMs) / 60_000)
  if (Math.abs(minutes) < 1) return { label: 'On time', tone: 'ok' }
  const isLater = minutes > 0
  const word = isLater ? (concern === 'late' ? 'late' : 'over') : 'early'
  const flagged = concern === 'late' ? isLater : !isLater
  return {
    label: `${formatWorkedMinutes(Math.abs(minutes))} ${word}`,
    tone: flagged ? 'warn' : 'muted',
  }
}

const DIFF_TONE: Record<DiffTone, string> = {
  ok: 'text-green-700 dark:text-green-400',
  warn: 'text-amber-700 dark:text-amber-400',
  muted: 'text-gray-600 dark:text-gray-300',
}

/**
 * Scheduled shift beside what actually happened: first clock-in against the
 * shift start, last clock-out against the shift end. Raw difference — the
 * "Late by" total below applies the grace period.
 */
function ScheduleVsActual({ attendance }: { attendance: Attendance }): React.ReactElement | null {
  const schedule = getScheduledWindow(attendance)
  if (!schedule) return null
  const firstIn = attendance.sessions?.[0]?.clockIn ?? attendance.clockIn
  const lastOut = attendance.clockOut
  const live = isCurrentlyClockedIn(attendance)
  const startDiff = firstIn
    ? describeDiff(new Date(firstIn).getTime(), schedule.start.getTime(), 'late')
    : null
  const endDiff = lastOut
    ? describeDiff(new Date(lastOut).getTime(), schedule.end.getTime(), 'early')
    : null
  const durationMinutes = Math.round((schedule.end.getTime() - schedule.start.getTime()) / 60_000)
  const endOffset = dayOffset(schedule.start.toISOString(), schedule.end.toISOString())
  const outOffset = firstIn && lastOut ? dayOffset(firstIn, lastOut) : 0

  return (
    <section aria-labelledby="att-schedule-heading">
      <h4
        id="att-schedule-heading"
        className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
      >
        Schedule vs actual
      </h4>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
              <th scope="col" className="px-3 py-2 font-medium">
                <span className="sr-only">Row</span>
              </th>
              <th scope="col" className="px-3 py-2 font-medium">Start</th>
              <th scope="col" className="px-3 py-2 font-medium">End</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 tabular-nums dark:divide-gray-700">
            <tr>
              <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                Scheduled
                <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                  {formatWorkedMinutes(durationMinutes)}
                </span>
              </th>
              <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">
                {formatDate(schedule.start.toISOString(), 'p')}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">
                {formatDate(schedule.end.toISOString(), 'p')}
                {endOffset > 0 && (
                  <span className="ml-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    +{endOffset}d
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                Actual
              </th>
              <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">
                {firstIn ? formatDate(firstIn, 'p') : <span className="text-gray-400">—</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">
                {lastOut ? (
                  <>
                    {formatDate(lastOut, 'p')}
                    {outOffset > 0 && (
                      <span className="ml-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        +{outOffset}d
                      </span>
                    )}
                  </>
                ) : live ? (
                  <span className="font-medium text-green-700 dark:text-green-400">On the clock</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                Difference
              </th>
              <td className={cn('whitespace-nowrap px-3 py-2 font-medium', startDiff ? DIFF_TONE[startDiff.tone] : 'text-gray-400')}>
                {startDiff?.label ?? '—'}
              </td>
              <td className={cn('whitespace-nowrap px-3 py-2 font-medium', endDiff ? DIFF_TONE[endDiff.tone] : 'text-gray-400')}>
                {endDiff?.label ?? '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}

interface SessionView {
  key: string
  clockIn?: string
  clockOut?: string
  breaks: AttendanceBreak[]
  workedMinutes?: number
  active: boolean
}

/** Prior completed sessions plus the record's own (current or only) session. */
function collectSessions(attendance: Attendance): SessionView[] {
  const prior: SessionView[] = (attendance.sessions ?? []).map((s, idx) => ({
    key: s._id ?? `prior-${idx}`,
    clockIn: s.clockIn,
    clockOut: s.clockOut,
    breaks: s.breaks,
    workedMinutes: s.workedMinutes,
    active: false,
  }))
  if (attendance.clockIn) {
    prior.push({
      key: 'current',
      clockIn: attendance.clockIn,
      clockOut: attendance.clockOut,
      breaks: attendance.breaks,
      // With no prior sessions the record's total is this session's total.
      workedMinutes:
        prior.length === 0 && attendance.clockOut ? attendance.totalWorkedMinutes : undefined,
      active: isCurrentlyClockedIn(attendance),
    })
  }
  return prior
}

function SessionsTable({ attendance }: { attendance: Attendance }): React.ReactElement {
  const sessions = collectSessions(attendance)
  const multi = sessions.length > 1

  return (
    <section aria-labelledby="att-sessions-heading">
      <h4
        id="att-sessions-heading"
        className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
      >
        {multi ? `Time on the clock · ${sessions.length} sessions` : 'Time on the clock'}
      </h4>
      {sessions.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No clock-in recorded for this shift.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
                {multi && (
                  <th scope="col" className="w-8 px-3 py-2 font-medium">
                    <span className="sr-only">Session</span>
                  </th>
                )}
                <th scope="col" className="px-3 py-2 font-medium">Clock-in</th>
                <th scope="col" className="px-3 py-2 font-medium">Clock-out</th>
                <th scope="col" className="px-3 py-2 font-medium">Breaks</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">Worked</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s, idx) => {
                const offset = dayOffset(s.clockIn, s.clockOut)
                const breakTotal = breakMinutes(s.breaks)
                return (
                  <tr key={s.key} className="border-t border-gray-200 dark:border-gray-700">
                    {multi && (
                      <th
                        scope="row"
                        className="px-3 py-2.5 text-left text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400"
                      >
                        {idx + 1}
                      </th>
                    )}
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-gray-900 dark:text-gray-100">
                      {s.clockIn ? formatDate(s.clockIn, 'EEE d MMM, p') : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums">
                      {s.clockOut ? (
                        <span className="text-gray-900 dark:text-gray-100">
                          {formatDate(s.clockOut, offset > 0 ? 'EEE d MMM, p' : 'p')}
                        </span>
                      ) : s.active ? (
                        <span className="font-medium text-green-700 dark:text-green-400">
                          In progress
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Not recorded</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-gray-600 dark:text-gray-400">
                      {s.breaks.length === 0 ? (
                        <span className="text-gray-400 dark:text-gray-500">None</span>
                      ) : (
                        <span title={s.breaks
                          .map((b) =>
                            `${formatDate(b.startTime, 'p')}${b.endTime ? ` – ${formatDate(b.endTime, 'p')}` : ' – ongoing'} (${b.isPaid ? 'paid' : 'unpaid'})`
                          )
                          .join('\n')}
                        >
                          {s.breaks.length} · {formatWorkedMinutes(breakTotal)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-gray-900 dark:text-gray-100">
                      {s.workedMinutes != null ? formatWorkedMinutes(s.workedMinutes) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function TimeSummary({ attendance }: { attendance: Attendance }): React.ReactElement {
  const live = isCurrentlyClockedIn(attendance)
  const late = attendance.lateByMinutes > 0
  const lateDeduction = attendance.deductions?.lateDeduction ?? 0
  const breakDeduction = attendance.deductions?.excessBreakDeduction ?? 0
  const totalDeduction = attendance.deductions?.totalDeduction ?? 0

  const items: Array<{ label: string; value: string; tone?: 'warn' | 'muted' }> = [
    {
      label: 'Worked',
      value: live && !attendance.totalWorkedMinutes ? 'In progress' : formatWorkedMinutes(attendance.totalWorkedMinutes),
      tone: live && !attendance.totalWorkedMinutes ? 'muted' : undefined,
    },
    { label: 'Paid', value: formatWorkedMinutes(attendance.paidMinutes) },
    { label: 'Unpaid breaks', value: formatWorkedMinutes(attendance.unpaidBreakMinutes ?? 0) },
    {
      label: 'Late by',
      value: late ? formatWorkedMinutes(attendance.lateByMinutes) : 'On time',
      tone: late ? 'warn' : 'muted',
    },
  ]

  return (
    <section aria-labelledby="att-summary-heading">
      <h4
        id="att-summary-heading"
        className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
      >
        Totals
      </h4>
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <dl className="grid grid-cols-2 divide-gray-200 sm:grid-cols-4 sm:divide-x dark:divide-gray-700">
          {items.map((item) => (
            <div key={item.label} className="px-3 py-2.5">
              <dt className="text-xs text-gray-500 dark:text-gray-400">{item.label}</dt>
              <dd
                className={cn(
                  'mt-0.5 font-semibold tabular-nums',
                  item.tone === 'warn'
                    ? 'text-amber-700 dark:text-amber-400'
                    : item.tone === 'muted'
                      ? 'font-medium text-gray-500 dark:text-gray-400'
                      : 'text-gray-900 dark:text-gray-100'
                )}
              >
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
        {totalDeduction !== 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-red-200 bg-red-50/60 px-3 py-2.5 text-sm dark:border-red-500/30 dark:bg-red-500/5">
            <span className="flex flex-wrap gap-x-3 text-red-800 dark:text-red-300">
              <span className="font-medium">Deductions</span>
              {lateDeduction > 0 && (
                <span className="tabular-nums">Late {formatWorkedMinutes(lateDeduction)}</span>
              )}
              {breakDeduction > 0 && (
                <span className="tabular-nums">
                  Excess break {formatWorkedMinutes(breakDeduction)}
                </span>
              )}
            </span>
            {/* Deductions are time, not money — payroll prices them at the hourly rate. */}
            <span className="font-semibold tabular-nums text-red-800 dark:text-red-300">
              −{formatWorkedMinutes(totalDeduction)} unpaid
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

function NotesBlock({ notes }: { notes: string }): React.ReactElement {
  return (
    <section aria-labelledby="att-notes-heading">
      <h4
        id="att-notes-heading"
        className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
      >
        Notes
      </h4>
      <div className="flex gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100">
        <Quote className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        <p className="whitespace-pre-line">{notes}</p>
      </div>
    </section>
  )
}

AttendanceDetailModal.displayName = 'AttendanceDetailModal'
